/* eslint-disable indent */
import jwtdecode from "jwt-decode";
import { isEmpty, get } from "lodash";
import store from "./store";
import userMe from "./services/user-me";
import initOrg from "./services/init-organization-info";
import { initializeOrganization } from "@/router-guard/organization";
import { getUserAccessProfile, canAccessAdminFeatures } from "@/router-guard/access";
import USER_TYPE_CONSTANTS from "@/constants/user";
import { isAutomatedReorderEnabled } from "@/utils/featureFlags";
import notification from "@/utils/notifications";
// import authService from "@/auth/auth.service";
import { getInstance } from "@/auth";

export default function (router) {
  router.beforeEach(async (to, from, next) => {
    const authService = await getInstance();
    if (!authService) {
      // Handle the case where authService is undefined
      console.error("Auth service is not available.");
      return;
    }
    const guardAction = async () => {
      if (!authService.isAuthenticated && to.name !== "callback") {
        await store.dispatch("clearLocalStore");
        await authService.loginWithRedirect();
        return;
      }

      // GET BIOTE_USER_DATA
      const userIdentity = store.getters["publicAPIModule/getUserIdentity"];
      const accessToken = await authService.getAccessTokenSilently();
      const internalUserInfo = await authService.user;
      const [isInternalUser, isBioteSalesUser] = await Promise.all([
        store.dispatch("usersModule/isInternalUser"),
        store.dispatch("usersModule/isBioteSalesUser"),
      ]);

      if (isInternalUser) {
        store.commit("publicAPIModule/setUserIdentity", internalUserInfo);
      }
      if (userIdentity === null && !isInternalUser) {
        localStorage.removeItem("userPrimaryState");
        const idProofingResponse = await store.dispatch(
          "publicAPIModule/identifyUserIdProofing",
          accessToken,
        );
        if (idProofingResponse === null) {
          return await store.dispatch("publicAPIModule/logoutUser");
        }
        const primaryState = get(idProofingResponse, "primaryState", "");
        localStorage.setItem("userPrimaryState", primaryState);
      }
      const { loginExpTime, loginAccessToken } = getLocalStorageValues();
      const validAuthorization = loginExpTime && loginAccessToken;
      const offCanvasMenuStatus = store.getters.getOffCanvasMenuStatus;

      if (offCanvasMenuStatus) {
        store.commit("toggleOffCanvasMenu", { toggle: false });
      }

      // For Callback and IDme process
      const bypassRoutes = ["callback", "IDMeCallback"];
      if (
        !validAuthorization &&
        (to.meta.onboarding || bypassRoutes.includes(to.name))
      ) {
        return next();
      }

      const isTokenExpired = checkTokenExpiration(loginExpTime);

      if (!validAuthorization || isTokenExpired) {
        handleExpiredToken(to, next);
        return;
      }

      await handleValidToken(to, next, {
        isInternalUser,
        isBioteSalesUser,
      });
    };
    if (!authService || !authService.isLoading) {
      return guardAction();
    }
    authService.$watch("isLoading", isLoading => {
      if (isLoading === false) {
        return guardAction();
      }
    });
  });
}

function getLocalStorageValues() {
  const exp = localStorage.getItem("exp");
  const accessToken = localStorage.getItem("access_token");
  return {
    loginExpTime: !["", null, undefined].includes(exp) ? exp : null,
    loginAccessToken: !["", null, undefined].includes(accessToken)
      ? accessToken
      : null,
    // loginIdToken: localStorage.getItem("id_token"),
    selectedVendor: localStorage.getItem("selected_vendor"),
    hydraState: localStorage.getItem("hydraState"),
    orgId: localStorage.getItem("orgId"),
  };
}

function checkTokenExpiration(loginExpTime) {
  const tokenExpiration = Number(loginExpTime);
  return tokenExpiration < Math.floor(Date.now() / 1000);
}

async function handleExpiredToken(to, next) {
  localStorage.callbackRoute = to.path;
  await store.dispatch("clearLocalStore");
  await store.dispatch("publicAPIModule/logoutUser");
}

async function validateAuth() {
  const { loginExpTime, loginAccessToken, selectedVendor } =
    getLocalStorageValues();
  const decodedToken = jwtdecode(loginAccessToken);

  if (!loginAccessToken || !loginExpTime) {
    localStorage.setItem("access_token", loginAccessToken);
    localStorage.setItem("exp", loginExpTime);
  }

  const myInfo = store.getters["usersModule/getMyInfo"];
  if (
    !myInfo ||
    Object.keys(myInfo).length === 0 ||
    Object.values(myInfo).includes(null)
  ) {
    await store.dispatch("usersModule/initializeCurrentLoggedInInfo", {
      exp: loginExpTime,
      access_token: loginAccessToken,
      decodedToken,
    });
    // Partially added setUserInfo here, need more time refactor the old code
    await store.dispatch("setUserInfo", decodedToken);
  }
  return { selectedVendor, decodedToken };
}

async function initializeNonInternalUser({ userInfo, decodedToken, access }) {
  const treatAsNonInternal =
    userInfo.userType !== "INTERNAL_USER" || access.isBioteSalesUser;

  if (treatAsNonInternal) {
    await initializeOrganization();

    // Initialization of orgId or vendorId
    await reInitLocalStorageDetails({
      decodedToken,
      access,
    });
  }

  // FETCHING THE ORGANIZATION USER DATA
  if (
    isEmpty(store.getters["usersModule/getMyUserInfo"]) &&
    treatAsNonInternal
  ) {
    await store.dispatch("usersModule/fetchMyUserInfo");
  }
}

async function handleValidToken(to, next, roleFlags = {}) {
  let { selectedVendor, decodedToken } = await validateAuth();
  const userInfo = get(decodedToken.sim, "user", {});
  let { isInternalUser: internalUserFlag, isBioteSalesUser: bioteSalesFlag } =
    roleFlags;

  if (typeof internalUserFlag === "undefined") {
    internalUserFlag = await store.dispatch("usersModule/isInternalUser");
  }

  if (typeof bioteSalesFlag === "undefined") {
    bioteSalesFlag = await store.dispatch("usersModule/isBioteSalesUser");
  }

  const accessProfile = getUserAccessProfile({
    decodedToken,
    internalUser: internalUserFlag,
    bioteSalesUser: bioteSalesFlag,
    selectedVendor,
  });
  const { userType, subject } = accessProfile;
  const hasVendorPageAccess = accessProfile.canAccessVendorFeatures;
  if (!hasVendorPageAccess) {
    selectedVendor = null;
    localStorage.removeItem("selected_vendor");
  }
  const isVendorView = hasVendorPageAccess && accessProfile.hasVendorView;
  const access = { ...accessProfile, isVendorView };
  const { isInternalUser, isSuperAdmin } = access;
  await initOrg.initializeUserRoles(subject);

  // Authenticated user navigation
  if (isInternalUser) {
    await internalUserNavigation(next, to);
    return;
  }
  await initializeNonInternalUser({
    userInfo,
    decodedToken,
    access,
  });

  let currentLocation = null;
  if (!isVendorView) {
    const result = await initOrg.fetchOrgInitialDetails({
      userType,
      isSimpatraSuperAdmin: isSuperAdmin,
    });
    if (result) {
      currentLocation = result.selectedLocation || "";
    }
  }

  if (to.path.includes("/search-product")) {
    const currentLocationId = localStorage.getItem("currentLocation");
    const patientId = to.params.patientId || to.query.patientId;
    if (patientId) {
      try {
        const patientData = await store.dispatch(
          "patientModule/fetchPatientDetailsById",
          { patientId },
        );
        const patientLocations = (patientData && patientData.locations) || [];
        const hasAccessToLocation = patientLocations.some(
          location => location === currentLocationId,
        );
        if (!hasAccessToLocation) {
          next(`/dashboard/${currentLocationId}/patient-chart/`);
        }
      } catch (error) {
        console.error("Failed to fetch patient details: ", error);
      }
    }
  }

  const isSimpatraAdminPage = to.matched.some(
    record =>
      record.meta.internalUser || record.meta.simpatraAdminAccessRequired,
  );
  // Validate when user accessing super admin page
  if (isSimpatraAdminPage) {
    if (canAccessAdminFeatures(access)) {
      next();
    } else {
      next("/");
    }
  } else if (
    isVendorView &&
    hasVendorPageAccess &&
    to.name &&
    to.meta.checkVendorAccessibility
  ) {
    next();
  } else if (isVendorView && hasVendorPageAccess) {
    next({
      name: "VendorOrderList",
      params: { vendorId: selectedVendor },
    });
  } else {
    const navigatePagesToHome = [
      "callback",
      "PracticeInformation",
      undefined,
      "Provider",
    ];
    if (
      to &&
      (navigatePagesToHome.includes(to.name) ||
        to.meta.checkVendorAccessibility)
    ) {
      if (!currentLocation) {
        currentLocation = localStorage.getItem("currentLocation");
      }
      next(`/dashboard/${currentLocation}/patient-chart/`);
    } else {
      next();
    }
  }
}

const reInitLocalStorageDetails = async ({ decodedToken, access }) => {
  const { isVendorView, userType, isSuperAdmin } = access;
  const { orgId, selectedVendor } = getLocalStorageValues();

  if (userType === USER_TYPE_CONSTANTS.organizationUser && !isVendorView) {
    // This is the org id of the current login, this is for security purpose
    // We always used decoded token to avoid using localStorage when they tamper the orgId
    const currentLogginOrgId = get(decodedToken.sim.details, "id", null);
    if (
      isSuperAdmin &&
      currentLogginOrgId !== orgId &&
      !isEmpty(orgId)
    ) {
      localStorage.setItem("orgId", orgId);
    } else {
      localStorage.setItem("orgId", currentLogginOrgId);
    }
  } else if (userType === USER_TYPE_CONSTANTS.vendorUser) {
    const vendorId = get(decodedToken.sim.details, "id", "");
    if (isEmpty(selectedVendor)) {
      localStorage.setItem("selected_vendor", vendorId);
    }
  }
};

// Being used in patient-chart route
export function hasPatientChartAccess() {
  return userMe.getMyRoles().isPatientAdmin;
}

// router guard when user is navigating to onboarding page
// This is being used with Onboarding pages
export async function allowToOnboard(to, from, next) {
  const practiceUserFormData =
    store.getters["onboardingModule/getPracticeUserFormData"];
  const authService = await getInstance();
  if (authService.isAuthenticated && !isEmpty(practiceUserFormData)) {
    next();
  } else if (authService.isAuthenticated && isEmpty(practiceUserFormData)) {
    const accessToken = await authService.getAccessTokenSilently();
    const response = await store.dispatch(
      "publicAPIModule/validateAuth0Token",
      { accessToken },
    );
    if (response === null) await store.dispatch("publicAPIModule/logoutUser");
    store.commit(
      "onboardingModule/appendDataToPracticeUserFormData",
      response.data,
    );
    next();
  } else {
    window.location.href = "/";
  }
}

// router guard for the Automated Reorders feature, currently disabled by default
// via VUE_APP_FEATURE_AUTOMATED_REORDER (see src/utils/featureFlags.js)
export function allowAutomatedReorderAccess(to, from, next) {
  if (isAutomatedReorderEnabled()) {
    next();
  } else {
    next({ name: "PrescriptionQueue", params: { locationId: to.params.locationId } });
  }
}

export async function allowUserToNavigateBulkOrder(to, from, next) {
  const bulkOrderVisibility =
    store.getters["marketplaceModule/getBulkOrderVisibility"];
  const authService = await getInstance();
  if (authService.isAuthenticated && bulkOrderVisibility) {
    next();
  } else {
    window.location.href = "/";
  }
}

async function internalUserNavigation(next, to) {
  const isInternalUserPage = to.matched.some(
    record => record.meta.internalUser,
  );
  if (isInternalUserPage) {
    // Check access for all features to determine best redirect
    const { FEATURES } = await import("@/constants/system-feature");

    const apiAccess = await store.dispatch("usersModule/getFeatureAccess", {
      feature: FEATURES.API_ACCESS,
    });

    const rolesAccess = await store.dispatch("usersModule/getFeatureAccess", {
      feature: FEATURES.ROLES_AND_PERMISSIONS,
    });

    const orgAccess = await store.dispatch("usersModule/getFeatureAccess", {
      feature: FEATURES.ORGANIZATION_MANAGEMENT,
    });

    // If user only has Organization Management access (Read-only users),
    // redirect them directly to Organization Management (but allow all org-related pages)
    const allowedRoutesForReadOnly = [
      "OrganizationManagement",
      "OrganizationDetails",
      "OrgManageLocations",
      "OrgManageUsers",
      "VendorRelationshipManager",
    ];
    if (!apiAccess && !rolesAccess && orgAccess) {
      if (!allowedRoutesForReadOnly.includes(to.name)) {
        next({ name: "OrganizationManagement" });
        return;
      }
    }

    const hasFeatureAccess = await store.dispatch("usersModule/getPageAccess", {
      feature: to.meta.featureName,
    });
    if (hasFeatureAccess) {
      next();
    } else {
      // Redirect based on available access
      // Priority: API -> Roles -> Organization Management
      if (apiAccess && apiAccess !== null) {
        next({ name: "BioteAPI" });
        return;
      }

      if (rolesAccess && rolesAccess !== null) {
        next({ name: "RolesAndPermissions" });
        return;
      }

      if (orgAccess && orgAccess !== null) {
        next({ name: "OrganizationManagement" });
        return;
      }

      // If no access to any admin panel features, redirect to vendor management
      next({ name: "VendorManagement" });
    }
    return;
  }
  next({ name: "VendorManagement" });
}
